import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import axios from 'axios';
import { useReceipt } from '../../hooks/useReceipt';
import ReceiptPreview from './ReceiptPreview';
import ReceiptModal from './ReceiptModal';  // POSレジと同じ領収書モーダル
import { printReceiptHTML } from './ReceiptPrint';



interface Transaction {
  id: number;
  timestamp: string;
  order_type: 'eatin' | 'takeout';
  payment_type: 'cash' | 'paypay';
  items: string | any[];
  subtotal: number;
  tax: number;
  total: number;
  received?: number;
  change?: number;
  status?: 'completed' | 'cancelled' | 'modified';
  modified_at?: string;
  cancelled_at?: string;
  // レシート再印刷用の追加フィールド（2025-11-15追加）
  discount?: number;
  discount_type?: string;
  discount_value?: number;
  final_total?: number;
  customer_id?: number;
  claim_id?: string;
  points?: number;
  receipt_number?: string;
  // 値引き表示用フィールド（2025-11-16追加）
  coupon_discount?: number;
  manual_discount?: number;
  discount_amount?: number;
  tax_mode?: 'internal' | 'external' | 'exempt';
  tax_rate?: number;
  is_tax_exempt?: boolean;
}

interface TransactionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  initialFilter?: 'all' | 'takeout';
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ isOpen, onClose, initialFilter = 'all' }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'cancel' | 'receipt' | null>(null);
  const { printReceipt, generatePDF } = useReceipt();
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [previewTransaction, setPreviewTransaction] = useState<Transaction | null>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'takeout'>(initialFilter);
  // POSレジと同じ領収書モーダル用の状態
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptTransaction, setSelectedReceiptTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTodayTransactions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShowReceiptModal(false);
      setSelectedReceiptTransaction(null);
      setShowReceiptPreview(false);
      setPreviewTransaction(null);
    }
  }, [isOpen]);

  const fetchTodayTransactions = async () => {
    setLoading(true);
    try {
      // 当日の取引履歴を取得（JST基準）
      const now = new Date();
      const jstDate = new Date(now.getTime() + 9 * 60 * 60 * 1000);
      const today = jstDate.toISOString().split('T')[0];
      console.log('Fetching transactions for date:', today);
      const response = await apiClient.get(`/transactions/daily?date=${today}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      alert('取引履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (transaction: Transaction, type: 'cancel' | 'receipt') => {
    setSelectedTransaction(transaction);
    setActionType(type);
    
    if (type === 'receipt') {
      // 領収書即座に発行
      handleReceiptReissue(transaction);
    } else {
      setShowConfirmDialog(true);
    }
  };

  const buildReceiptTransaction = (transaction: Transaction) => {
    let parsedItems: any[] = [];
    try {
      const itemsSource = typeof transaction.items === 'string' ? JSON.parse(transaction.items) : transaction.items;
      parsedItems = Array.isArray(itemsSource) ? itemsSource : [];
    } catch {
      parsedItems = [];
    }

    const itemsText = typeof transaction.items === 'string' ? transaction.items : JSON.stringify(parsedItems);
    const hasBentoToken = itemsText.includes('BENTO_');
    const effectiveOrderType: 'eatin' | 'takeout' = hasBentoToken ? 'takeout' : transaction.order_type;

    const subtotalNum = Number(transaction.subtotal || 0);
    const taxNum = Number(transaction.tax || 0);
    let effectiveTaxRate = effectiveOrderType === 'takeout' ? 8 : 10;
    if (subtotalNum > 0 && Number.isFinite(subtotalNum) && Number.isFinite(taxNum)) {
      const calculatedRate = Math.round((taxNum / subtotalNum) * 100);
      if (calculatedRate > 0 && calculatedRate <= 100) {
        effectiveTaxRate = calculatedRate;
      }
    }

    return {
      ...transaction,
      items: parsedItems,
      order_type: effectiveOrderType,
      tax_rate: effectiveTaxRate,
      tax_mode: transaction.tax_mode || 'internal',
      is_tax_exempt: transaction.is_tax_exempt || false
    };
  };

  // レシート表示処理（クリック同期で空ウィンドウを開いてポップアップブロック回避）
  const handleReprint = (transaction: Transaction) => {
    const popupWindow = window.open('about:blank', 'receipt-preview', 'popup,width=520,height=740');
    if (!popupWindow) {
      alert('ポップアップを許可してから再度印刷してください');
      return;
    }

    try {
      popupWindow.document.open();
      popupWindow.document.write(`
        <!DOCTYPE html>
        <html lang="ja">
        <head><meta charset="UTF-8"><title>Receipt Diagnostics</title>
        <style>@media print { #diag-root, #diag-log, .diag-root, .diag-log, .no-print { display: none !important; } body { margin: 0; } }</style>
        </head>
        <body style="font-family:sans-serif;padding:16px;">
          <div id="diag-root">
            <p>読み込み中…</p>
            <pre id="diag-log">DIAG START (history receipt)</pre>
          </div>
        </body>
        </html>
      `);
      popupWindow.document.close();
    } catch (error) {
      const message = (error as any)?.message || String(error);
      popupWindow.location.href = `data:text/html;charset=utf-8,${encodeURIComponent(`
        <!DOCTYPE html>
        <html lang="ja">
        <head><meta charset="UTF-8"><title>Receipt Diagnostics Fallback</title>
        <style>@media print { #diag-root, #diag-log, .diag-root, .diag-log, .no-print { display: none !important; } body { margin: 0; } }</style>
        </head>
        <body style="font-family:sans-serif;padding:16px;">
          <div id="diag-root">
            <p>読み込み中…</p>
            <pre id="diag-log">DIAG START (fallback)\n${message}</pre>
          </div>
        </body>
        </html>
      `)}`;
    }
    popupWindow.focus?.();

    const transactionData = buildReceiptTransaction(transaction);
    let openCount = 0;
    const appendDiag = (line: string) => {
      const root = popupWindow.document.getElementById('diag-root');
      if (!root) return;
      const log = popupWindow.document.getElementById('diag-log');
      if (log) {
        log.textContent = `${log.textContent || ''}\n${line}`;
      }
    };
    const originalOpen = window.open;
    (window as any).open = () => {
      openCount += 1;
      return popupWindow;
    };

    void (async () => {
      try {
        const r: any = await printReceiptHTML(transactionData);
        if (typeof r === 'string') {
          popupWindow.document.open();
          popupWindow.document.write(r);
          popupWindow.document.close();
        } else {
          if (popupWindow.document.getElementById('diag-root')) {
            appendDiag('status: completed');
            appendDiag(`printReceiptHTML return type: ${typeof r}`);
            appendDiag(`result exists: ${r ? 'yes' : 'no'}`);
            appendDiag(`openCount: ${openCount}`);
          }
        }
        popupWindow.focus?.();
      } catch (error) {
        if (popupWindow.document.getElementById('diag-root')) {
          appendDiag('status: failed');
          appendDiag(`openCount: ${openCount}`);
          appendDiag(`name: ${(error as any)?.name || '-'}`);
          appendDiag(`message: ${(error as any)?.message || String(error)}`);
        }
        console.error('レシート再印刷エラー:', error);
      } finally {
        (window as any).open = originalOpen;
      }
    })();
  };

  // 領収書再発行処理（POSレジと同じReceiptModalを使用）
  const handleReceiptReissue = (transaction: Transaction) => {
    console.log('領収書再発行 - POSレジと同じReceiptModalを使用');

    const transactionData = buildReceiptTransaction(transaction);

    // POSレジと同じReceiptModalを表示（値引き・クーポン情報を含む）
    setSelectedReceiptTransaction(transactionData);
    setShowReceiptModal(true);
  };

  const handleCancelTransaction = async () => {
    if (!selectedTransaction) return;
    
    try {
      setLoading(true);
      
      // 取引をキャンセル
      await apiClient.post(`/transactions/${selectedTransaction.id}/cancel`, {
        reason: '顧客要求による取消'
      });
      
      // 成功メッセージと新規会計の確認
      const startNewTransaction = window.confirm(
        '取引を取り消しました。\n新しい会計を開始しますか？'
      );
      
      // リスト更新
      await fetchTodayTransactions();
      setShowConfirmDialog(false);
      setSelectedTransaction(null);
      
      // 新規会計を開始する場合は履歴画面を閉じる
      if (startNewTransaction) {
        onClose(); // 履歴画面を閉じてPOSレジ画面に戻る
      }
    } catch (error) {
      console.error('Cancel transaction error:', error);
      alert('取引の取消に失敗しました');
    } finally {
      setLoading(false);
    }
  };


  const formatDateTime = (timestamp: string) => {
    // タイムスタンプはUTCで保存されているので、UTC文字列として解釈
    // 'Z'を追加してUTCとして明示的に解釈させる
    const utcTimestamp = timestamp.includes('Z') ? timestamp : `${timestamp.replace(' ', 'T')}Z`;
    const date = new Date(utcTimestamp);

    // JST時刻に変換してローカルメソッドで取得
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  };

  const formatItems = (itemsData: string | any[]) => {
    try {
      const items = typeof itemsData === 'string' ? JSON.parse(itemsData) : itemsData;
      if (Array.isArray(items)) {
        return items.map((item: any) => `${item.name}×${item.quantity}`).join(', ');
      }
      return '';
    } catch {
      return '';
    }
  };

  const getStatusBadge = (transaction: Transaction) => {
    if (transaction.status === 'cancelled') {
      return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">取消済</span>;
    }
    if (transaction.status === 'modified') {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">訂正済</span>;
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[90%] h-[85%] flex flex-col">
        {/* Header */}
        <div className="p-5 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex justify-between items-center rounded-t-lg">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold">会計履歴（本日分）</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterMode('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterMode === 'all'
                    ? 'bg-white text-blue-600'
                    : 'bg-blue-500 text-white hover:bg-blue-400'
                }`}
              >
                全て
              </button>
              <button
                onClick={() => setFilterMode('takeout')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterMode === 'takeout'
                    ? 'bg-white text-blue-600'
                    : 'bg-blue-500 text-white hover:bg-blue-400'
                }`}
              >
                🥡 テイクのみ
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-xl text-gray-500">読み込み中...</div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-xl text-gray-500">本日の取引履歴はありません</div>
            </div>
          ) : (
            <div className="space-y-4">
              {transactions
                .filter(t => filterMode === 'all' || t.order_type === 'takeout')
                .map((transaction) => (
                <div 
                  key={transaction.id} 
                  className={`bg-white border rounded-lg p-5 hover:shadow-md transition-shadow ${
                    transaction.status === 'cancelled' ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-lg font-bold">#{transaction.id}</span>
                        <span className="text-gray-600">{formatDateTime(transaction.timestamp)}</span>
                        {getStatusBadge(transaction)}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">種別:</span>
                          <span className="ml-2 font-medium">
                            {transaction.order_type === 'eatin' ? '店内' : 'テイクアウト'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">支払:</span>
                          <span className="ml-2 font-medium">
                            {transaction.payment_type === 'cash' ? '現金' : 'PayPay'}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">商品:</span>
                          <span className="ml-2">{formatItems(transaction.items)}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center justify-between">
                        <div className="text-xl font-bold text-blue-600">
                          合計: ¥{transaction.total.toLocaleString()}
                        </div>
                        {transaction.payment_type === 'cash' && transaction.received && (
                          <div className="text-sm text-gray-600">
                            受取: ¥{transaction.received.toLocaleString()} / 
                            お釣り: ¥{transaction.change?.toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2 ml-4">
                      {transaction.status !== 'cancelled' && (
                        <>
                          <button
                            onClick={() => handleReprint(transaction)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            disabled={loading}
                          >
                            レシート
                          </button>
                          <button
                            onClick={() => handleAction(transaction, 'receipt')}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            disabled={loading}
                          >
                            領収書
                          </button>
                          <button
                            onClick={() => handleAction(transaction, 'cancel')}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            disabled={loading}
                          >
                            取消
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Summary */}
        <div className="p-5 border-t bg-gray-50 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-gray-600">
              本日の取引数: <span className="font-bold text-xl">{transactions.filter(t => t.status !== 'cancelled').length}</span>件
            </div>
            <div className="text-gray-600">
              本日の売上合計: <span className="font-bold text-xl text-blue-600">
                ¥{transactions
                  .filter(t => t.status !== 'cancelled')
                  .reduce((sum, t) => sum + t.total, 0)
                  .toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-xl font-bold mb-4">
              取引を取り消しますか？
            </h3>
            
            <div className="mb-4 p-4 bg-gray-50 rounded">
              <div className="text-sm space-y-1">
                <div>取引番号: #{selectedTransaction.id}</div>
                <div>金額: ¥{selectedTransaction.total.toLocaleString()}</div>
                <div>商品: {formatItems(selectedTransaction.items)}</div>
              </div>
            </div>

            <p className="text-amber-600 text-sm mb-4">
              ※ 取消後、新しい会計を開始できます。
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                キャンセル
              </button>
              <button
                onClick={handleCancelTransaction}
                className="px-4 py-2 text-white rounded-lg bg-red-600 hover:bg-red-700"
                disabled={loading}
              >
                取消実行
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modify Transaction Dialog - REMOVED */}
      {false && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-4">取引の訂正</h3>
            
            <div className="mb-4 p-4 bg-gray-50 rounded">
              <div className="text-sm space-y-1">
                <div>取引番号: #{selectedTransaction.id}</div>
                <div>元の金額: ¥{selectedTransaction.total.toLocaleString()}</div>
              </div>
            </div>

            {/* Items Editor */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">商品内容の編集</h4>
              <div className="space-y-3">
                {modifiedItems.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                    <div className="flex-1">
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-600">¥{item.price.toLocaleString()}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          const newItems = [...modifiedItems];
                          if (newItems[index].quantity > 1) {
                            newItems[index].quantity--;
                          } else {
                            newItems.splice(index, 1);
                          }
                          setModifiedItems(newItems);
                        }}
                        className="w-8 h-8 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        −
                      </button>
                      <span className="w-12 text-center font-bold">{item.quantity}</span>
                      <button
                        onClick={() => {
                          const newItems = [...modifiedItems];
                          newItems[index].quantity++;
                          setModifiedItems(newItems);
                        }}
                        className="w-8 h-8 bg-blue-500 text-white rounded-full hover:bg-blue-600"
                      >
                        +
                      </button>
                    </div>
                    <div className="text-right min-w-[100px] font-bold">
                      ¥{(item.price * item.quantity).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="border-t pt-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>小計</span>
                  <span className="font-bold">
                    ¥{modifiedItems.reduce((sum, item) => sum + item.price * item.quantity, 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>(内消費税)</span>
                  <span className="font-bold">
                    ¥{Math.floor(modifiedItems.reduce((sum, item) => sum + item.price * item.quantity, 0) - (modifiedItems.reduce((sum, item) => sum + item.price * item.quantity, 0) / 1.1)).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-xl font-bold pt-2 border-t">
                  <span>合計</span>
                  <span className="text-blue-600">
                    ¥{(modifiedItems.reduce((sum, item) => sum + item.price * item.quantity, 0) * 1.1).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowModifyDialog(false);
                  setModifiedItems([]);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                キャンセル
              </button>
              <button
                onClick={async () => {
                  try {
                    setLoading(true);
                    
                    // 内税計算
                    const total = modifiedItems.reduce((sum, item) => sum + item.price * item.quantity, 0); // 既に税込価格
                    const tax = Math.floor(total - (total / 1.1));
                    const subtotal = total - tax;
                    
                    await apiClient.post(`/transactions/${selectedTransaction.id}/modify`, {
                      order_type: selectedTransaction.order_type,
                      payment_type: selectedTransaction.payment_type,
                      subtotal,
                      tax,
                      total,
                      items: modifiedItems,
                      received: selectedTransaction.received,
                      change: selectedTransaction.payment_type === 'cash' ? (selectedTransaction.received || 0) - total : null
                    });
                    
                    alert('取引を訂正しました');
                    
                    setShowModifyDialog(false);
                    setModifiedItems([]);
                    await fetchTodayTransactions();
                  } catch (error) {
                    console.error('Modify transaction error:', error);
                    alert('取引の訂正に失敗しました');
                  } finally {
                    setLoading(false);
                  }
                }}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                disabled={loading || modifiedItems.length === 0}
              >
                訂正を確定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 領収書モーダル（POSレジと同じコンポーネント） */}
      {showReceiptModal && selectedReceiptTransaction && (
        <ReceiptModal
          transaction={selectedReceiptTransaction}
          onClose={() => {
            setShowReceiptModal(false);
            setSelectedReceiptTransaction(null);
          }}
          isOpen={showReceiptModal}
        />
      )}

      {/* レシートプレビューモーダル */}
      {showReceiptPreview && previewTransaction && (
        <ReceiptPreview
          transaction={previewTransaction}
          isOpen={showReceiptPreview}
          onPrint={async () => {
            try {
              setLoading(true);
              const result = await printReceipt(previewTransaction);

              if (result?.success) {
                if (result?.method === 'html') {
                  alert('プリンター未接続のため、HTMLレシートを生成しました');
                } else {
                  alert('レシートを再発行しました');
                }
                setShowReceiptPreview(false);
              } else {
                alert(`レシート再発行エラー: ${result?.error || result?.message || 'プリンター接続エラー'}`);
              }
            } catch (error) {
              console.error('Reprint error:', error);
              alert('レシート再発行に失敗しました');
            } finally {
              setLoading(false);
            }
          }}
          onSavePDF={async () => {
            try {
              setLoading(true);
              await generatePDF(previewTransaction);
              alert('PDFが保存されました');
            } catch (error) {
              console.error('PDF generation error:', error);
              alert(`PDF生成エラー: ${error}`);
            } finally {
              setLoading(false);
            }
          }}
          onClose={() => {
            setShowReceiptPreview(false);
            setPreviewTransaction(null);
          }}
        />
      )}
    </div>
  );
};

export default TransactionHistory;
